import { Injectable } from '@angular/core';
import { FilingFormState } from './filing-form-state.service';
import { SheetProjector, SheetProjectionContext } from './sheet-projector';
import { role3StockExchangeProjector } from './projectors/role3-stock-exchange.projector';
import { role7AuditorsReportProjector } from './projectors/role7-auditors-report.projector';
import { role4DirectorsReportProjector } from './projectors/role4-directors-report.projector';
import { role5StatementByDirectorsProjector } from './projectors/role5-statement-by-directors.projector';

/**
 * VisibleProjectionRegistry
 *
 * Central registry of pure {@link SheetProjector}s keyed by sheet key. The
 * submit pipeline (`SubmitForValidationComponent`) calls {@link projectAll}
 * once, just before the FE→BE transforms, to strip conditionally-hidden fields
 * out of every registered sheet's slice — so orphaned hidden-field values
 * (backend hydration, or a parent selector the user later changed) are not
 * submitted as XBRL facts.
 *
 * Mirrors {@link PureValidatorRegistry} + the `PURE_VERDICT_SHEETS` allow-list:
 * a sheet is only projected once its pure projector is registered here, so the
 * leak fix rolls out **one sheet at a time**, each parity-checked against its
 * mounted component's visibility rules. Sheets with no registered projector
 * are left untouched (full data — current behaviour), so registration is
 * strictly additive and safe.
 */
@Injectable({ providedIn: 'root' })
export class VisibleProjectionRegistry {
  private readonly projectors = new Map<keyof FilingFormState, SheetProjector>();

  /**
   * Projector rollout roster. Add one sheet at a time after verifying its
   * projection matches the mounted component's visible-field set. Kept as a
   * flat array (not dynamic discovery) so the covered sheets are trivially
   * auditable in review.
   */
  private readonly REGISTERED: ReadonlyArray<SheetProjector> = [
    role3StockExchangeProjector,
    role7AuditorsReportProjector,
    role4DirectorsReportProjector,
    role5StatementByDirectorsProjector,
  ];

  constructor() {
    for (const projector of this.REGISTERED) {
      this.register(projector);
    }
  }

  /**
   * Register a projector for its declared sheet key. Throws on a duplicate —
   * each sheet has exactly one canonical projector, so a collision usually
   * means an accidental double-registration.
   */
  register(projector: SheetProjector): void {
    if (this.projectors.has(projector.sheetKey)) {
      throw new Error(
        `[VisibleProjectionRegistry] duplicate projector for sheet "${String(projector.sheetKey)}"`
      );
    }
    this.projectors.set(projector.sheetKey, projector);
  }

  /** Look up a projector by sheet key; undefined when none is registered. */
  get(sheetKey: keyof FilingFormState): SheetProjector | undefined {
    return this.projectors.get(sheetKey);
  }

  /** Sheet keys covered by registered projectors (snapshot — do not mutate). */
  registeredSheets(): ReadonlySet<keyof FilingFormState> {
    return new Set(this.projectors.keys());
  }

  /**
   * Apply every registered projector to its sheet's slice in `allSheetsData`,
   * **in place** (consistent with the sibling prep steps in the submit
   * pipeline). Sheets without a projector, or whose slice is missing/non-object,
   * are skipped. Never throws on a missing sheet.
   */
  projectAll(
    allSheetsData: Record<string, any>,
    context: SheetProjectionContext
  ): void {
    for (const [sheetKey, projector] of this.projectors) {
      const slice = allSheetsData[sheetKey as string];
      if (slice && typeof slice === 'object') {
        allSheetsData[sheetKey as string] = projector.project(slice, context);
      }
    }
  }
}
